/**
 * WASM vs Tone.js Performance Benchmark
 *
 * Compares performance metrics between WASM and Tone.js synthesizers
 */

import { useState, useRef } from 'react';
import * as Tone from 'tone';
import { MonoSynthBridge } from '~/lib/wasm/mono-synth-bridge';

interface BenchmarkResults {
  synthType: 'tone' | 'wasm';
  scenario: string;
  avgCpuTime: number; // ms per 128 samples
  peakCpuTime: number; // ms
  totalTime: number; // ms
  notesProcessed: number;
  memoryUsed?: number; // MB
}

export default function WasmBenchmark() {
  const [results, setResults] = useState<BenchmarkResults[]>([]);
  const [running, setRunning] = useState(false);
  const [currentTest, setCurrentTest] = useState('');

  const audioContextRef = useRef<AudioContext | null>(null);

  const initAudioContext = async () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
      await Tone.start();
      Tone.setContext(audioContextRef.current);
    }
    return audioContextRef.current;
  };

  const benchmarkToneJs = async (scenario: string, noteCount: number): Promise<BenchmarkResults> => {
    const context = await initAudioContext();
    const synth = new Tone.MonoSynth().connect(context.destination);

    const times: number[] = [];
    const startMemory = (performance as any).memory?.usedJSHeapSize || 0;
    const startTime = performance.now();

    // Run the benchmark
    for (let i = 0; i < noteCount; i++) {
      const noteStart = performance.now();

      // Trigger note with scheduled time to avoid timing conflicts
      const now = Tone.now();
      synth.triggerAttackRelease('C4', 0.05, now + 0.001); // Very short notes with tiny delay

      // Measure time
      const noteEnd = performance.now();
      times.push(noteEnd - noteStart);

      // Small delay between notes to prevent overlapping triggers
      await new Promise(resolve => setTimeout(resolve, 15));
    }

    const totalTime = performance.now() - startTime;
    const endMemory = (performance as any).memory?.usedJSHeapSize || 0;

    // Cleanup
    synth.dispose();

    return {
      synthType: 'tone',
      scenario,
      avgCpuTime: times.reduce((a, b) => a + b, 0) / times.length,
      peakCpuTime: Math.max(...times),
      totalTime,
      notesProcessed: noteCount,
      memoryUsed: (endMemory - startMemory) / 1024 / 1024 // Convert to MB
    };
  };

  const benchmarkWasm = async (scenario: string, noteCount: number): Promise<BenchmarkResults> => {
    const context = await initAudioContext();
    const bridge = new MonoSynthBridge(context);

    await bridge.initialize();
    bridge.connect(context.destination);

    const times: number[] = [];
    const startMemory = (performance as any).memory?.usedJSHeapSize || 0;
    const startTime = performance.now();

    // Run the benchmark
    for (let i = 0; i < noteCount; i++) {
      const noteStart = performance.now();

      // Trigger note (MIDI note 60 = C4)
      bridge.noteOn(60, 100);

      // Small delay to simulate note duration
      await new Promise(resolve => setTimeout(resolve, 10));
      bridge.noteOff();

      const noteEnd = performance.now();
      times.push(noteEnd - noteStart);

      // Small delay to prevent blocking
      if (i % 100 === 0) {
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }

    const totalTime = performance.now() - startTime;
    const endMemory = (performance as any).memory?.usedJSHeapSize || 0;

    // Cleanup
    bridge.dispose();

    return {
      synthType: 'wasm',
      scenario,
      avgCpuTime: times.reduce((a, b) => a + b, 0) / times.length,
      peakCpuTime: Math.max(...times),
      totalTime,
      notesProcessed: noteCount,
      memoryUsed: (endMemory - startMemory) / 1024 / 1024 // Convert to MB
    };
  };

  const runBenchmarks = async () => {
    setRunning(true);
    setResults([]);

    const scenarios = [
      { name: 'Single Notes (100)', count: 100 },
      { name: 'Rapid Fire (500)', count: 500 },
      { name: 'Stress Test (1000)', count: 1000 },
    ];

    for (const scenario of scenarios) {
      // Test Tone.js
      setCurrentTest(`${scenario.name} - Tone.js`);
      const toneResult = await benchmarkToneJs(scenario.name, scenario.count);
      setResults(prev => [...prev, toneResult]);

      // Wait a bit between tests
      await new Promise(resolve => setTimeout(resolve, 500));

      // Test WASM
      setCurrentTest(`${scenario.name} - WASM`);
      const wasmResult = await benchmarkWasm(scenario.name, scenario.count);
      setResults(prev => [...prev, wasmResult]);

      // Wait between scenarios
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    setCurrentTest('');
    setRunning(false);
  };

  const calculateImprovement = (toneTime: number, wasmTime: number): string => {
    const improvement = ((toneTime - wasmTime) / toneTime) * 100;
    return improvement > 0
      ? `${improvement.toFixed(1)}% faster`
      : `${Math.abs(improvement).toFixed(1)}% slower`;
  };

  // Group results by scenario
  const groupedResults = results.reduce((acc, result) => {
    if (!acc[result.scenario]) {
      acc[result.scenario] = {};
    }
    acc[result.scenario][result.synthType] = result;
    return acc;
  }, {} as Record<string, Record<string, BenchmarkResults>>);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">WASM vs Tone.js Benchmark</h1>

      <div className="mb-6">
        <button
          onClick={runBenchmarks}
          disabled={running}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed hover:bg-blue-700 transition"
        >
          {running ? `Running: ${currentTest}` : 'Run Benchmarks'}
        </button>
      </div>

      {results.length > 0 && (
        <div className="space-y-8">
          <h2 className="text-2xl font-semibold">Results</h2>

          {Object.entries(groupedResults).map(([scenario, synthResults]) => {
            const toneResult = synthResults['tone'];
            const wasmResult = synthResults['wasm'];

            if (!toneResult || !wasmResult) return null;

            return (
              <div key={scenario} className="border rounded-lg p-6 bg-white shadow-sm">
                <h3 className="text-xl font-semibold mb-4">{scenario}</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Tone.js Results */}
                  <div className="space-y-2">
                    <h4 className="font-semibold text-lg text-blue-600">Tone.js</h4>
                    <div className="space-y-1 text-sm">
                      <p>Avg CPU Time: <span className="font-mono">{toneResult.avgCpuTime.toFixed(3)}ms</span></p>
                      <p>Peak CPU Time: <span className="font-mono">{toneResult.peakCpuTime.toFixed(3)}ms</span></p>
                      <p>Total Time: <span className="font-mono">{toneResult.totalTime.toFixed(0)}ms</span></p>
                      {toneResult.memoryUsed && (
                        <p>Memory Used: <span className="font-mono">{toneResult.memoryUsed.toFixed(2)}MB</span></p>
                      )}
                    </div>
                  </div>

                  {/* WASM Results */}
                  <div className="space-y-2">
                    <h4 className="font-semibold text-lg text-green-600">WASM</h4>
                    <div className="space-y-1 text-sm">
                      <p>Avg CPU Time: <span className="font-mono">{wasmResult.avgCpuTime.toFixed(3)}ms</span></p>
                      <p>Peak CPU Time: <span className="font-mono">{wasmResult.peakCpuTime.toFixed(3)}ms</span></p>
                      <p>Total Time: <span className="font-mono">{wasmResult.totalTime.toFixed(0)}ms</span></p>
                      {wasmResult.memoryUsed && (
                        <p>Memory Used: <span className="font-mono">{wasmResult.memoryUsed.toFixed(2)}MB</span></p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Comparison */}
                <div className="mt-4 p-4 bg-gray-50 rounded">
                  <h4 className="font-semibold mb-2">Performance Comparison</h4>
                  <div className="space-y-1 text-sm">
                    <p>Avg Time: WASM is <span className="font-semibold text-green-600">
                      {calculateImprovement(toneResult.avgCpuTime, wasmResult.avgCpuTime)}
                    </span></p>
                    <p>Total Time: WASM is <span className="font-semibold text-green-600">
                      {calculateImprovement(toneResult.totalTime, wasmResult.totalTime)}
                    </span></p>
                    {toneResult.memoryUsed && wasmResult.memoryUsed && (
                      <p>Memory: WASM uses <span className="font-semibold text-green-600">
                        {calculateImprovement(toneResult.memoryUsed, wasmResult.memoryUsed)} less
                      </span></p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Overall Summary */}
          <div className="border-t-4 border-green-600 rounded-lg p-6 bg-gradient-to-r from-green-50 to-blue-50">
            <h3 className="text-xl font-bold mb-4">Overall Performance Summary</h3>

            {results.filter(r => r.synthType === 'wasm').length > 0 && (
              <div className="space-y-2">
                <p className="text-lg">
                  WASM achieved an average performance improvement of{' '}
                  <span className="font-bold text-2xl text-green-600">
                    {(() => {
                      // Calculate average improvement from pairs of results (tone, wasm)
                      const improvements = [];
                      for (let i = 0; i < results.length; i += 2) {
                        const toneResult = results[i];
                        const wasmResult = results[i + 1];
                        if (toneResult && wasmResult) {
                          improvements.push(
                            ((toneResult.avgCpuTime - wasmResult.avgCpuTime) / toneResult.avgCpuTime) * 100
                          );
                        }
                      }
                      const avgImprovement = improvements.reduce((a, b) => a + b, 0) / improvements.length;

                      return `${avgImprovement.toFixed(1)}%`;
                    })()}
                  </span>
                </p>

                <p className="text-sm text-gray-600">
                  {(() => {
                    // Calculate average improvement from pairs of results (tone, wasm)
                    const improvements = [];
                    for (let i = 0; i < results.length; i += 2) {
                      const toneResult = results[i];
                      const wasmResult = results[i + 1];
                      if (toneResult && wasmResult) {
                        improvements.push(
                          ((toneResult.avgCpuTime - wasmResult.avgCpuTime) / toneResult.avgCpuTime) * 100
                        );
                      }
                    }
                    const avgImprovement = improvements.reduce((a, b) => a + b, 0) / improvements.length;

                    const multiplier = avgImprovement >= 100 ? (avgImprovement / 100 + 1).toFixed(1) : '1.0';

                    return avgImprovement >= 100
                      ? `✅ WASM is ${multiplier}x faster - Proceed with Phase 2!`
                      : '⚠️ Performance improvement is below 2x threshold';
                  })()}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
